import { useEffect, useState, type JSX } from "react";

import type { UserPresence } from "../api/types.js";

/**
 * Runder Avatar mit Status-Punkt. Ohne Profilbild stehen die Initialen darin, in einer
 * Farbe, die sich aus dem Namen ergibt — so sieht jede Person immer gleich aus.
 */

export const PRESENCE_LABELS: Record<UserPresence, string> = {
  AVAILABLE: "Verfügbar",
  AWAY: "Abwesend",
  BUSY: "Beschäftigt",
  OFFLINE: "Offline",
};

const PRESENCE_COLORS: Record<UserPresence, string> = {
  AVAILABLE: "bg-green-500",
  AWAY: "bg-amber-400",
  BUSY: "bg-red-500",
  OFFLINE: "bg-slate-400",
};

// Farben mit ausreichendem Kontrast zu weißer Schrift.
const INITIAL_COLORS = [
  "bg-rose-600",
  "bg-orange-600",
  "bg-amber-700",
  "bg-emerald-700",
  "bg-teal-700",
  "bg-sky-700",
  "bg-indigo-600",
  "bg-violet-600",
  "bg-fuchsia-700",
];

export function initialsOf(displayName: string): string {
  // Der erste Buchstabe eines Namensteils, nicht dessen erstes Zeichen: sonst würde aus
  // "Assistenz (Agent)" ein "A(".
  const letters = displayName
    .trim()
    .split(/\s+/)
    .map((part) => /\p{L}|\p{N}/u.exec(part)?.[0] ?? "")
    .filter((letter) => letter.length > 0);

  if (letters.length === 0) {
    return "?";
  }
  const first = letters[0] as string;
  const last = letters.length > 1 ? (letters.at(-1) as string) : "";
  return (first + last).toUpperCase();
}

export function colorFor(seed: string): string {
  let hash = 0;
  for (const char of seed) {
    hash = (hash * 31 + char.codePointAt(0)!) % 100_000;
  }
  return INITIAL_COLORS[hash % INITIAL_COLORS.length] as string;
}

const SIZES = {
  sm: { box: "h-7 w-7 text-[10px]", dot: "h-2.5 w-2.5", cross: "text-[7px]" },
  md: { box: "h-9 w-9 text-xs", dot: "h-3 w-3", cross: "text-[8px]" },
  lg: { box: "h-16 w-16 text-lg", dot: "h-4 w-4", cross: "text-[10px]" },
} as const;

export function Avatar({
  displayName,
  avatarUrl,
  presence,
  size = "md",
}: {
  displayName: string;
  avatarUrl?: string | null;
  /** Ohne Angabe bleibt der Status-Punkt weg. */
  presence?: UserPresence | null;
  size?: keyof typeof SIZES;
}): JSX.Element {
  const dimensions = SIZES[size];
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => setImageFailed(false), [avatarUrl]);

  return (
    <span className="relative inline-flex shrink-0">
      {avatarUrl && !imageFailed ? (
        <img
          src={avatarUrl}
          alt=""
          onError={() => setImageFailed(true)}
          className={`${dimensions.box} rounded-full object-cover`}
        />
      ) : (
        <span
          aria-hidden="true"
          className={`${dimensions.box} ${colorFor(displayName)} flex items-center justify-center rounded-full font-semibold text-white`}
        >
          {initialsOf(displayName)}
        </span>
      )}

      {presence ? (
        <span
          title={PRESENCE_LABELS[presence]}
          className={[
            dimensions.dot,
            PRESENCE_COLORS[presence],
            "absolute bottom-0 right-0 flex items-center justify-center rounded-full",
            "border-2 border-surface-raised font-bold leading-none text-white",
          ].join(" ")}
        >
          <span className="sr-only">{PRESENCE_LABELS[presence]}</span>
          {presence === "OFFLINE" ? (
            <span aria-hidden="true" className={dimensions.cross}>
              ✕
            </span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}
